<template>
  <div class="animated fadeIn">

    <!-- Модалка создания/редактирования карточки -->
    <b-modal
        id="student-document-modal"
        :title="form.id ? 'Редактирование документа' : 'Новый документ'"
        size="md"
        no-close-on-backdrop
        no-enforce-focus
        hide-footer>
      <b-form @submit.prevent="submit">
        <b-form-group label="Слушатель (ФИО)">
          <div class="sd-typeahead">
            <b-input
                v-model="studentQuery"
                autocomplete="off"
                placeholder="Введите ФИО для поиска"
                @input="onStudentSearch"
                @focus="showStudentMenu = true"
                @blur="hideStudentMenuSoon"/>
            <div v-if="showStudentMenu && studentQuery.trim().length >= 2" class="sd-typeahead__menu">
              <div v-if="studentsLoading" class="sd-typeahead__item sd-typeahead__muted">Поиск…</div>
              <template v-else>
                <button
                    v-for="u in studentOptions"
                    :key="u.id"
                    type="button"
                    class="sd-typeahead__item"
                    @mousedown.prevent="selectStudent(u)">
                  {{ optionLabel(u) }}
                </button>
                <div v-if="!studentOptions.length" class="sd-typeahead__item sd-typeahead__muted">
                  Ничего не найдено
                </div>
              </template>
            </div>
          </div>
          <small v-if="form.studentId && selectedStudentLabel" class="text-success">
            <i class="fa fa-check"></i> {{ selectedStudentLabel }}
          </small>
        </b-form-group>

        <b-form-group label="Название">
          <b-input v-model="form.title" placeholder="Например: Договор о практике" required/>
        </b-form-group>

        <b-form-group label="Описание">
          <b-textarea v-model="form.description" rows="3" placeholder="Краткое описание / инструкция"/>
        </b-form-group>

        <b-form-group label="Дедлайн загрузки">
          <b-form-input v-model="form.deadline" type="date"/>
        </b-form-group>

        <b-form-group label="Шаблон (Word/PDF)">
          <b-form-file
              v-model="form.templateFile"
              accept=".pdf,.doc,.docx"
              browse-text="Выбрать"
              placeholder="Файл не выбран"
              drop-placeholder="Перетащите файл сюда…"/>
          <small v-if="form.id && currentTemplateName" class="text-muted">
            Текущий шаблон: {{ currentTemplateName }} (загрузка нового заменит его)
          </small>
        </b-form-group>

        <div class="text-right">
          <b-button variant="white" size="sm" @click="$bvModal.hide('student-document-modal')">Отмена</b-button>
          <b-button type="submit" variant="primary" size="sm" :disabled="saving">
            <i v-if="saving" class="fa fa-spinner fa-spin"></i>
            Сохранить
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <BreadCrumbs title="Документы слушателей" :items="[{title: 'Главная', url: '/admin/'}]"/>

    <div class="wrapper wrapper-content">
      <ibox>
        <ibox-title>
          <b-row align-v="center">
            <b-col md="6">
              <el-select
                  v-model="filterStudentId"
                  filterable
                  clearable
                  remote
                  reserve-keyword
                  placeholder="Фильтр по ФИО слушателя"
                  :remote-method="searchFilterStudents"
                  :loading="filterStudentsLoading"
                  style="width: 100%"
                  @change="loadDocuments()"
                  @clear="onFilterClear">
                <el-option
                    v-for="u in filterStudentOptions"
                    :key="u.id"
                    :label="optionLabel(u)"
                    :value="u.id"/>
              </el-select>
            </b-col>
            <b-col md="6" class="text-right m-t-xs-mobile">
              <b-button variant="primary" size="sm" @click="openCreate">
                <i class="fa fa-plus"></i> Добавить документ
              </b-button>
            </b-col>
          </b-row>
        </ibox-title>

        <ibox-content>
          <b-table striped hover bordered responsive :fields="fields" :items="documents" show-empty
                   empty-text="Документы не найдены">
            <template v-slot:cell(student)="data">
              <a href="#" @click.prevent="openEdit(data.item)" class="client-link">
                {{ data.item.student ? data.item.student.fullname : '—' }}
              </a>
            </template>

            <template v-slot:cell(title)="data">
              {{ data.item.title }}
              <div v-if="data.item.description" class="text-muted small">{{ data.item.description }}</div>
            </template>

            <template v-slot:cell(deadline)="data">
              <span v-if="data.item.deadline" :class="{'text-danger': data.item.isOverdue}">
                {{ data.item.deadline | formatDate('DD.MM.YYYY') }}
              </span>
              <span v-else class="text-muted">—</span>
            </template>

            <template v-slot:cell(template)="data">
              <b-button
                  v-if="data.item.hasTemplate"
                  variant="white" size="sm"
                  @click="downloadFile(data.item.templateDownloadPath, data.item.templateName)">
                <i class="fa fa-download"></i> Скачать
              </b-button>
              <span v-else class="text-muted">—</span>
            </template>

            <template v-slot:cell(submission)="data">
              <div v-if="data.item.hasSubmission">
                <b-button
                    variant="white" size="sm"
                    @click="downloadFile(data.item.submissionDownloadPath, data.item.submissionName)">
                  <i class="fa fa-download"></i> {{ data.item.submissionName || 'Скачать' }}
                </b-button>
                <div class="text-muted small m-t-xs">
                  <i class="fa fa-clock-o"></i> {{ data.item.submittedAt | formatDate('DD.MM.YYYY HH:mm') }}
                </div>
              </div>
              <b-badge v-else variant="warning">Не загружено</b-badge>
            </template>

            <template v-slot:cell(actions)="data">
              <b-btn-group>
                <b-button variant="info" size="sm" @click="openEdit(data.item)" title="Редактировать">
                  <i class="fa fa-pencil"></i>
                </b-button>
                <b-button variant="danger" size="sm" @click="deleteDocument(data.item)" title="Удалить">
                  <i class="fa fa-trash"></i>
                </b-button>
              </b-btn-group>
            </template>
          </b-table>
        </ibox-content>

        <ibox-footer v-if="hasMore">
          <div class="text-center">
            <b-button variant="white" size="sm" @click="loadMore">Показать ещё</b-button>
          </div>
        </ibox-footer>
      </ibox>
    </div>
  </div>
</template>

<script>
import BreadCrumbs from "../BreadCrumbs";
import Ibox from "../Ibox/Ibox";
import IboxTitle from "../Ibox/IboxTitle";
import IboxContent from "../Ibox/IboxContent";
import IboxFooter from "../Ibox/IboxFooter";

const ALLOWED_EXT = ['pdf', 'doc', 'docx'];
const PER_PAGE = 30;

export default {
  name: "StudentDocumentList",
  components: {BreadCrumbs, Ibox, IboxTitle, IboxContent, IboxFooter},
  data() {
    return {
      documents: [],
      page: 1,
      hasMore: false,

      filterStudentId: null,
      filterStudentOptions: [],
      filterStudentsLoading: false,

      studentOptions: [],
      studentsLoading: false,
      studentQuery: '',
      showStudentMenu: false,
      selectedStudentLabel: '',
      searchTimer: null,

      saving: false,
      currentTemplateName: null,
      form: this.emptyForm(),

      fields: [
        {key: 'student', label: 'ФИО'},
        {key: 'title', label: 'Название'},
        {key: 'deadline', label: 'Дедлайн'},
        {key: 'template', label: 'Шаблон'},
        {key: 'submission', label: 'Подписанная копия'},
        {key: 'actions', label: ''},
      ],
    }
  },
  mounted() {
    this.loadDocuments();
  },
  methods: {
    emptyForm() {
      return {id: null, studentId: null, title: '', description: '', deadline: null, templateFile: null};
    },
    optionLabel(u) {
      return u.username ? `${u.fullname} (${u.username})` : u.fullname;
    },
    loadDocuments() {
      this.page = 1;
      const params = {page: this.page, hideLoader: true};
      if (this.filterStudentId) params.student = this.filterStudentId;
      this.axios.get('student_documents', {params}).then(res => {
        const items = res.data || [];
        this.documents = items;
        this.hasMore = items.length >= PER_PAGE;
      });
    },
    loadMore() {
      this.page += 1;
      const params = {page: this.page, hideLoader: true};
      if (this.filterStudentId) params.student = this.filterStudentId;
      this.axios.get('student_documents', {params}).then(res => {
        const items = res.data || [];
        this.documents = this.documents.concat(items);
        this.hasMore = items.length >= PER_PAGE;
      });
    },
    onFilterClear() {
      this.filterStudentId = null;
      this.loadDocuments();
    },
    async _searchUsers(query) {
      // Предпочтительно — endpoint фичи (доступен ROLE_ADMIN). Если его ещё нет на сервере (404) —
      // откатываемся на users/list (доступен супер-админу). Интерсептор резолвит ошибки в error.response.
      const res = await this.axios.get('student_documents/students', {params: {q: query, hideLoader: true}});
      if (res && res.status < 400 && Array.isArray(res.data)) {
        return res.data;
      }
      const fallback = await this.axios.get('users/list', {params: {fullname: query, hideLoader: true}});
      return (fallback && fallback.data && fallback.data.data) ? fallback.data.data : [];
    },
    onStudentSearch() {
      this.form.studentId = null;
      this.selectedStudentLabel = '';
      this.showStudentMenu = true;
      clearTimeout(this.searchTimer);
      const q = (this.studentQuery || '').trim();
      if (q.length < 2) {
        this.studentOptions = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.searchStudents(q), 300);
    },
    selectStudent(u) {
      this.form.studentId = u.id;
      this.selectedStudentLabel = this.optionLabel(u);
      this.studentQuery = this.optionLabel(u);
      this.showStudentMenu = false;
    },
    hideStudentMenuSoon() {
      setTimeout(() => { this.showStudentMenu = false; }, 150);
    },
    searchStudents(query) {
      if (!query) return;
      this.studentsLoading = true;
      this._searchUsers(query)
          .then(list => {
            this.studentOptions = list;
          })
          .finally(() => {
            this.studentsLoading = false;
          });
    },
    searchFilterStudents(query) {
      if (!query) return;
      this.filterStudentsLoading = true;
      this._searchUsers(query)
          .then(list => {
            this.filterStudentOptions = list;
          })
          .finally(() => {
            this.filterStudentsLoading = false;
          });
    },
    openCreate() {
      this.form = this.emptyForm();
      this.currentTemplateName = null;
      this.studentOptions = [];
      this.studentQuery = '';
      this.selectedStudentLabel = '';
      this.showStudentMenu = false;
      this.$bvModal.show('student-document-modal');
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        studentId: row.student ? row.student.id : null,
        title: row.title,
        description: row.description,
        deadline: row.deadline ? String(row.deadline).slice(0, 10) : null,
        templateFile: null,
      };
      this.currentTemplateName = row.templateName || null;
      const label = row.student ? this.optionLabel(row.student) : '';
      this.studentOptions = row.student ? [row.student] : [];
      this.studentQuery = label;
      this.selectedStudentLabel = label;
      this.showStudentMenu = false;
      this.$bvModal.show('student-document-modal');
    },
    async submit() {
      if (!this.form.studentId) {
        this.$toasted.global.appError('Выберите слушателя');
        return;
      }
      if (!this.form.title) {
        this.$toasted.global.appError('Введите название документа');
        return;
      }
      if (this.form.templateFile) {
        const ext = (this.form.templateFile.name.split('.').pop() || '').toLowerCase();
        if (ALLOWED_EXT.indexOf(ext) === -1) {
          this.$toasted.global.appError('Шаблон: допустимы только файлы Word и PDF (.pdf, .doc, .docx)');
          return;
        }
      }

      this.saving = true;
      try {
        const payload = {
          student: this.form.studentId,
          title: this.form.title,
          description: this.form.description || null,
          deadline: this.form.deadline || null,
        };

        let id = this.form.id;
        if (id) {
          await this.axios.put(`student_documents/${id}`, payload);
        } else {
          const res = await this.axios.post('student_documents', payload);
          id = res.data.id;
        }

        if (this.form.templateFile) {
          const fd = new FormData();
          fd.append('file', this.form.templateFile);
          await this.axios.post(`student_documents/${id}/template`, fd, {
            headers: {'Content-Type': 'multipart/form-data'}
          });
        }

        this.$bvModal.hide('student-document-modal');
        this.$toasted.global.appSuccess(this.form.id ? 'Документ обновлён' : 'Документ создан');
        this.loadDocuments();
      } catch (e) {
        this.$toasted.global.appError('Не удалось сохранить документ');
      } finally {
        this.saving = false;
      }
    },
    deleteDocument(row) {
      const who = row.student ? row.student.fullname : '';
      this.$bvModal.msgBoxConfirm(`Удалить документ «${row.title}»${who ? ' у ' + who : ''}?`, {
        okTitle: 'Удалить',
        cancelTitle: 'Отмена',
        okVariant: 'danger',
      }).then(ok => {
        if (!ok) return;
        this.axios.delete(`student_documents/${row.id}`).then(() => {
          this.$toasted.global.appSuccess('Документ удалён');
          this.loadDocuments();
        }).catch(() => {
          this.$toasted.global.appError('Не удалось удалить документ');
        });
      });
    },
    downloadFile(path, fileName) {
      if (!path) return;
      this.axios.get(path, {responseType: 'blob'}).then(res => {
        const blob = new Blob([res.data], {type: res.headers['content-type'] || 'application/octet-stream'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }).catch(() => {
        this.$toasted.global.appError('Не удалось скачать файл');
      });
    }
  }
}
</script>

<style scoped>
.m-t-xs-mobile {
  margin-top: 5px;
}
.sd-typeahead {
  position: relative;
}
.sd-typeahead__menu {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 2px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}
.sd-typeahead__item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}
.sd-typeahead__item:hover {
  background: #f2f7f5;
}
.sd-typeahead__muted {
  color: #9aa0a6;
  cursor: default;
}
</style>
